import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import EntHero from '../../components/enterprise/EntHero';
import EntStatCard from '../../components/enterprise/EntStatCard';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import EntEmptyState from '../../components/enterprise/EntEmptyState';
import { IconArrowRight } from '../../components/enterprise/entIcons';

function renderLeaderboardRows(entries, unit) {
  return entries.slice(0, 5).map((u, i) => (
    <li key={u.userId}>
      <span className="ent-rank" aria-label={`Rank ${i + 1}`}>#{i + 1}</span>
      <span className="ent-name">{u.name || u.email || '—'}</span>
      <span className="ent-metric">
        <span className="ent-metric-value">{u.value}</span>
        <span className="ent-metric-unit">{unit}</span>
      </span>
    </li>
  ));
}

function Leaderboard({ title, rows, unit }) {
  return (
    <div className="ent-leaderboard-card">
      <h3 className="ent-leaderboard-title">{title}</h3>
      {(rows || []).length === 0 ? (
        <p className="ent-empty">No activity recorded yet.</p>
      ) : (
        <ul className="ent-leaderboard-list">{renderLeaderboardRows(rows, unit)}</ul>
      )}
    </div>
  );
}

export default function EnterpriseDashboardPage() {
  const { enterpriseId } = useParams();
  const navigate = useNavigate();
  const base = `/enterprise-app/${enterpriseId}`;
  const [data, setData] = useState(null);
  const [seats, setSeats] = useState(null);
  const [invites, setInvites] = useState([]);
  const [teams, setTeams] = useState([]);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activityScoreOpen, setActivityScoreOpen] = useState(false);

  useEffect(() => {
    if (!enterpriseId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      api.get('/api/enterprise/dashboard', { params: { enterpriseId } }),
      api.get('/api/enterprise/seats', { params: { enterpriseId } }).catch(() => null),
      api.get('/api/enterprise/invites', { params: { enterpriseId } }).catch(() => null),
      api.get('/api/enterprise/teams', { params: { enterpriseId } }).catch(() => null),
      api.get('/api/enterprise/branding', { params: { enterpriseId } }).catch(() => null),
    ])
      .then(([dashRes, seatsRes, invitesRes, teamsRes, brandingRes]) => {
        if (cancelled) return;
        setData(dashRes.data);
        setSeats(seatsRes?.data?.capacity || null);
        setInvites(invitesRes?.data?.invites || []);
        setTeams(teamsRes?.data?.teams || []);
        setBranding(brandingRes?.data || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load dashboard');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enterpriseId]);

  const pendingInvites = invites.filter((invite) => invite.status === 'pending').length;
  const hasSeatPool = seats?.source === 'entitlement' && (seats?.purchased?.total || 0) > 0;
  const brandingComplete = Boolean(
    branding?.branding?.logoDataUrl || branding?.branding?.primaryColour
  );
  const isNewWorkspace =
    (data?.totalEmployees || 0) <= 1 &&
    teams.length === 0 &&
    pendingInvites === 0 &&
    (data?.contactsAddedThisMonth || 0) === 0;

  const actions = useMemo(() => {
    if (!data) return [];
    const items = [];
    if (teams.length === 0) {
      items.push({
        to: `${base}/teams`,
        title: 'Create a team',
        lead: 'Group people so you can manage membership and reporting in one place.',
      });
    }
    if (pendingInvites === 0 && (data.totalEmployees || 0) < 2) {
      items.push({
        to: `${base}/invites`,
        title: 'Invite colleagues',
        lead: 'They join as Members. A member place is reserved from your included capacity.',
      });
    }
    if (!brandingComplete) {
      items.push({
        to: `${base}/branding`,
        title: 'Add company branding',
        lead: 'A logo and colour appear on company cards while branding access is live.',
      });
    }
    const memberPlacesLeft = seats?.members?.remaining ?? seats?.remaining?.total ?? 0;
    if (hasSeatPool && memberPlacesLeft > 0) {
      items.push({
        to: `${base}/seats`,
        title: 'Use remaining member places',
        lead: `${memberPlacesLeft} member ${memberPlacesLeft === 1 ? 'place remains' : 'places remaining'}.`,
      });
    }
    if (hasSeatPool && memberPlacesLeft === 0 && pendingInvites === 0) {
      items.push({
        to: `${base}/seats`,
        title: 'Member places are full',
        lead: 'New invites need a free member place. Free one, or ask the owner to add capacity.',
      });
    }
    return items.slice(0, 4);
  }, [base, brandingComplete, data, hasSeatPool, pendingInvites, seats, teams.length]);

  if (loading) {
    return (
      <div className="ent-content">
        <EntHero title="Overview" lead="A snapshot of this company’s Velox workspace." />
        <EntSkeleton variant="cards" count={4} />
        <EntSkeleton variant="block" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ent-content">
        <EntHero title="Overview" />
        <div className="ent-flash ent-flash-error" role="alert">{error}</div>
      </div>
    );
  }

  const d = data || {};
  const insights = d.insights || [];
  const leaderboards = d.leaderboards || { contacts: [], cardTaps: [], meetingInvites: [] };
  const teamComparison = d.teamComparison || [];
  const topConnector = d.topConnectorThisMonth;
  const mostActiveDay = d.mostActiveDay;
  const remainingSeats = hasSeatPool
    ? (seats?.members?.remaining ?? seats?.remaining?.total ?? 0)
    : null;

  return (
    <div className="ent-content">
      <EntHero
        eyebrow={branding?.companyName || undefined}
        title="Overview"
        lead={
          isNewWorkspace
            ? 'This workspace is ready to set up. Invite people, create a team, and add branding when you are ready.'
            : 'People, seats, invitations and networking activity for this company.'
        }
      />

      <div className="ent-stat-row">
        <EntStatCard
          label="People"
          value={d.totalEmployees ?? 0}
          hint={`${d.activeEmployees ?? 0} active in the last 30 days`}
          accent
          to={`${base}/members`}
        />
        <EntStatCard
          label="Seats"
          value={
            hasSeatPool
              ? `${seats.members?.used ?? seats.assigned?.total ?? 0} / ${seats.members?.total ?? seats.purchased?.total ?? 0}`
              : 'No pool'
          }
          hint={
            hasSeatPool
              ? `${remainingSeats} member ${remainingSeats === 1 ? 'place' : 'places'} remaining`
              : 'Member capacity appears after Teams is active'
          }
          to={`${base}/seats`}
        />
        <EntStatCard
          label="Pending invites"
          value={pendingInvites}
          hint={pendingInvites ? 'Waiting to be accepted' : 'None outstanding'}
          to={`${base}/invites`}
        />
        <EntStatCard
          label="Contacts this month"
          value={d.contactsAddedThisMonth ?? 0}
          hint={`${d.cardTapsThisMonth ?? 0} cards shared`}
          to={`${base}/contacts`}
        />
      </div>

      {actions.length > 0 && (
        <section className="ent-section">
          <h2 className="ent-section-title">Recommended next steps</h2>
          <div className="ent-action-list">
            {actions.map((item) => (
              <Link key={item.to + item.title} to={item.to} className="ent-action-item">
                <span>
                  <strong>{item.title}</strong>
                  <span>{item.lead}</span>
                </span>
                <span className="ent-action-item-arrow" aria-hidden="true">
                  <IconArrowRight />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="ent-section">
        <h2 className="ent-section-title">Workspace</h2>
        <div className="ent-highlights">
          <div className="ent-card">
            <div className="ent-card-label">Teams</div>
            <div className="ent-card-value">{teams.length}</div>
            <div className="ent-card-sub">
              {teams.length ? 'Open Teams to manage membership' : 'No teams yet'}
            </div>
          </div>
          <div className="ent-card">
            <div className="ent-card-label">Branding</div>
            <div className="ent-card-value ent-card-value-sm">
              {brandingComplete ? 'Set up' : 'Not started'}
            </div>
            <div className="ent-card-sub">Company cards, not personal profiles</div>
          </div>
          {mostActiveDay ? (
            <div className="ent-card">
              <div className="ent-card-label">Most active day</div>
              <div className="ent-card-value ent-card-value-sm">{mostActiveDay}</div>
            </div>
          ) : null}
          {topConnector && topConnector.contactsAdded > 0 ? (
            <div className="ent-card">
              <div className="ent-card-label">Top connector this month</div>
              <div className="ent-card-value ent-card-value-sm">
                {topConnector.name || topConnector.email}
              </div>
              <div className="ent-card-sub">{topConnector.contactsAdded} contacts</div>
            </div>
          ) : null}
        </div>
      </section>

      {insights.length > 0 && (
        <section className="ent-section">
          <h2 className="ent-section-title">Insights</h2>
          <div className="ent-insights">
            {insights.map((text, i) => (
              <div key={i} className="ent-insight-card">{text}</div>
            ))}
          </div>
        </section>
      )}

      <section className="ent-section">
        <div className="ent-activity-score-header">
          <h2 className="ent-section-title">Leaderboards</h2>
          <button
            type="button"
            className="ent-activity-score-trigger"
            onClick={() => setActivityScoreOpen((open) => !open)}
            aria-label="How activity score is calculated"
            aria-expanded={activityScoreOpen}
          >
            <span className="ent-activity-score-icon" aria-hidden="true">i</span>
          </button>
          {activityScoreOpen ? (
            <div className="ent-activity-score-popover" role="dialog" aria-label="Activity score explanation">
              <p className="ent-activity-score-desc">
                Activity score weights contacts added (30%), cards shared (25%), meeting invites (25%) and active days (20%).
              </p>
            </div>
          ) : null}
        </div>
        <div className="ent-leaderboards">
          <Leaderboard title="Most contacts added" rows={leaderboards.contacts} unit="contacts" />
          <Leaderboard title="Most cards shared" rows={leaderboards.cardTaps} unit="cards" />
          <Leaderboard title="Most meeting invites" rows={leaderboards.meetingInvites} unit="invites" />
        </div>
      </section>

      <section className="ent-section">
        <h2 className="ent-section-title">Team comparison</h2>
        {teamComparison.length === 0 ? (
          <EntEmptyState
            title="No teams to compare yet"
            lead="Create a team and add members to see how groups compare on contacts, cards and invites."
            action={(
              <Link to={`${base}/teams`} className="ent-btn-primary">Go to Teams</Link>
            )}
          />
        ) : (
          <>
            <div className="ent-table-wrap ent-hide-sm">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Members</th>
                    <th>Contacts</th>
                    <th>Cards shared</th>
                    <th>Meeting invites</th>
                    <th>Avg activity</th>
                  </tr>
                </thead>
                <tbody>
                  {teamComparison.map((t) => (
                    <tr
                      key={t.teamId}
                      className="ent-row-clickable"
                      onClick={() => navigate(`${base}/teams/${t.teamId}`)}
                    >
                      <td>{t.teamName}</td>
                      <td>{t.memberCount}</td>
                      <td>{t.contactsAdded}</td>
                      <td>{t.cardTaps}</td>
                      <td>{t.meetingInvites}</td>
                      <td>{t.avgActivityScore}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ent-stack">
              {teamComparison.map((t) => (
                <button
                  type="button"
                  key={t.teamId}
                  className="ent-stack-card"
                  onClick={() => navigate(`${base}/teams/${t.teamId}`)}
                >
                  <strong>{t.teamName}</strong>
                  <div className="ent-stack-meta">
                    <span>{t.memberCount} members</span>
                    <span>{t.contactsAdded} contacts</span>
                    <span>{t.cardTaps} cards</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
